import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  InputLabel,
  Button,
  TextField,
  IconButton,
  Divider,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { editOrganization } from "../../store/actions/organizationAction";
import {
  isHaveHTMLElement,
  sanitizeInput,
} from "../../helpers/inputProcessing";
import { TypographySmall } from "../customComponent";
import { colorStyling } from "../../helpers/color";
import dayjs from "dayjs";

const EditOrganization = ({ open, onClose, organization }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("organization");
  const { t: tCommon } = useTranslation();

  const [organizationName, setOrganizationName] = useState(organization.name);
  const [nameValidation, setNameValidation] = useState("");
  const [viewerNumber, setViewerNumber] = useState(
    organization.screenQuota ?? "1"
  );
  const [subscriptionDate, setSubscriptionDate] = useState(
    organization.subscriptionEndAt
      ? dayjs(organization.subscriptionEndAt).format("YYYY-MM-DD")
      : null
  );

  const handleClickClose = () => {
    onClose(false);
    setNameValidation("");
  };

  const handleChangeViewerNumber = (number) => {
    let sanitizedNumber = number.toString().replace(/^0+/, "") || "0";

    if (sanitizedNumber === "") sanitizedNumber = "0";

    const min = 1;
    const max = 1000;
    sanitizedNumber = +sanitizedNumber;
    if (sanitizedNumber < min) sanitizedNumber = min;
    if (sanitizedNumber > max) sanitizedNumber = max;

    setViewerNumber(sanitizedNumber);
  };

  const handleChangeCheckbox = (event) => {
    if (event.target.checked) {
      setSubscriptionDate(
        dayjs(organization.subscriptionEndAt ?? new Date()).format("YYYY-MM-DD")
      );
    } else {
      setSubscriptionDate(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (organizationName.trim() === "") {
      setNameValidation(tCommon("validation.fieldMustNotEmpty"));
      setOrganizationName("");
      return;
    }
    if (isHaveHTMLElement(organizationName)) {
      setNameValidation(tCommon("validation.noHTMLAllowed"));
      return;
    }

    const payload = {
      id: organization.id,
      payload: {},
    };

    if (organizationName.trim() !== organization.name)
      payload.payload["name"] = sanitizeInput(organizationName.trim());

    if (viewerNumber !== organization.screenQuota)
      payload.payload["screensQuota"] = +viewerNumber;

    let subsDatePayload = subscriptionDate
      ? new Date(subscriptionDate + "T23:59:59").toISOString()
      : null;
    if (subsDatePayload !== organization.subscriptionEndAt)
      payload.payload["subscriptionEndAt"] = subsDatePayload;

    dispatch(editOrganization(payload));
  };

  return (
    <Dialog maxWidth="sm" fullWidth onClose={handleClickClose} open={open}>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {t("editDialogTitle")}
          <IconButton onClick={handleClickClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <Box>
            <InputLabel>{t("form.nameLabel")}</InputLabel>
            <TextField
              fullWidth
              margin="dense"
              value={organizationName}
              onChange={(e) => {
                setOrganizationName(e.target.value);
                setNameValidation("");
              }}
              onKeyDown={(e) => e.stopPropagation()}
            />
            <TypographySmall sx={{ color: colorStyling.red }}>
              {nameValidation}
            </TypographySmall>
          </Box>
          <Box sx={{ mt: 1 }}>
            <InputLabel>{t("form.viewerLimitLabel")}</InputLabel>
            <TextField
              fullWidth
              margin="dense"
              type="number"
              value={viewerNumber}
              onChange={(e) => {
                handleChangeViewerNumber(e.target.value);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              inputProps={{
                min: 1,
                max: 1000,
              }}
            />
          </Box>

          <Box>
            <InputLabel>
              {t("form.subscriptionLabel")}
              <Checkbox
                value={subscriptionDate}
                checked={subscriptionDate}
                onChange={handleChangeCheckbox}
              />
            </InputLabel>
            {subscriptionDate && (
              <TextField
                fullWidth
                margin="dense"
                type="date"
                value={subscriptionDate}
                onChange={(e) => {
                  setSubscriptionDate(
                    dayjs(new Date(e.target.value)).format("YYYY-MM-DD")
                  );
                }}
                onKeyDown={(e) => e.stopPropagation()}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClickClose} variant="outlined" fullWidth>
            {t("form.cancelBtn")}
          </Button>
          <Button type="submit" variant="contained" fullWidth>
            {t("form.saveBtn")}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default EditOrganization;
